import React, { useEffect, useState } from "react";
import { Container, Box, Typography, Card, CardContent, CircularProgress } from "@mui/material";
import { MyBlockIcon, MyEmailIcon, MyWarningIcon } from "../../Assets/svg/MyIcons";
import { getAdminEmailAction } from "../../Actions/SettingAction";
import { Link } from "react-router-dom";
import netcluesLogo from "../../Assets/netclues-logo.gif";


const IpBlockedPage = ({ loading, message }) => {
	const [blockMessage, setBlockMessage] = useState(message || "");
	const [adminEmail, setAdminEmail] = useState("");

	useEffect(() => {
		// Get message from localStorage if not passed as prop
		if (!message) {
			const storedMessage = localStorage.getItem("ipBlockedMessage");
			if (storedMessage) {
				setBlockMessage(storedMessage);
			}
		}

		getAdminEmailAction()
		.then((response) => {
			if (response) {
				setAdminEmail(response);
			}
		})
		.catch((error) => {
			console.error("Error fetching admin email:", error);
		});
	}, [message]);

	// Prevent going back to other pages
	useEffect(() => {
		const handlePopState = (e) => {
			e.preventDefault();
			window.history.pushState(null, "", window.location.pathname);
		};

		// Push initial state
		window.history.pushState(null, "", window.location.pathname);
		window.addEventListener("popstate", handlePopState);

		// Clear any stored tokens to prevent bypassing
		localStorage.removeItem("token");
		localStorage.removeItem("userData");

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, []);



	return (
		<Box
			className="position-relative d-flex align-items-center justify-content-center"
			sx={{
				minHeight: "100vh",
				overflow: "hidden",
				p: 3,
			}}
		>
			{/* Soft Background Gradient - Red/Orange tint for blocked state */}
			<Box
				className="position-absolute w-100 h-100 top-0 start-0"
				sx={{
					background: "linear-gradient(135deg, #d6e0ff, #f6f9fc 60%)",
					opacity: 0.95,
					zIndex: -2,
				}}
			/>

			{/* Floating Decorative Blobs */}
			<Box
				className="position-absolute rounded-circle"
				sx={{
					width: "22rem",
					height: "22rem",
					top: "20%",
					left: "20%",
					background: "rgba(80,120,200,0.20)",
					filter: "blur(50px)",
					animation: "blobPulse 7s infinite",
					zIndex: -1,
				}}
			/>

			<Box
				className="position-absolute rounded-circle"
				sx={{
					width: "19rem",
					height: "19rem",
					bottom: "15%",
					right: "22%",
					background: "rgba(80,200,170,0.20)",
					filter: "blur(55px)",
					animation: "blobPulse 8s infinite",
					animationDelay: "1.5s",
					zIndex: -1,
				}}
			/>

			<Container>
				{loading ? (
					// Loading Spinner
					<div
						className="d-flex align-items-center justify-content-center"
						style={{ height: "90vh" }}
					>
						<CircularProgress
							sx={{ color: "#344d95" }}
							size={60}
						/>
					</div>
				) : (
					<Card
						className="shadow"
						sx={{
							maxWidth: 620,
							mx: "auto",
							borderRadius: 3,
							overflow: "hidden",
							backdropFilter: "blur(6px)",
						}}
					>
						{/* Top Border Highlight - Red gradient */}
						<Box
							sx={{
								height: 8,
								background: "linear-gradient(135deg, #9bb8ff, #e3e8ff)",
							}}
						/>

						<CardContent sx={{ p: 3, textAlign: "center" }}>
							{/* Block Icon Section */}
							<Box
								sx={{
									width: 95,
									height: 95,
									mx: "auto",
									borderRadius: "50%",
									bgcolor: "#e9f0ff",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									boxShadow: 2,
									mb: 3,
									animation: "scaleIn 0.5s ease-out",
								}}
							>
								<MyBlockIcon
									size={42}
									color="#005a56"
								/>
							</Box>

							{/* Title */}
							<Typography
								variant="h4"
								fontWeight="bold"
								gutterBottom
								sx={{ color: "#0a2332" }}
							>
								IP Blocked
							</Typography>

							{/* Subtitle */}
							<Typography
								variant="body1"
								sx={{ color: "#1e4946", mb: 3, fontWeight: 600 }}
							>
								{blockMessage || "Your IP has been blocked due to too many failed attempts."}
							</Typography>

							{/* Divider with label */}
							<Box className="d-flex align-items-center my-3 text-secondary">
								<Box sx={{ flex: 1, borderBottom: "1px solid #b8d4d1" }}></Box>
								<Typography
									variant="h5"
									sx={{ mx: 2, color: "#0d4b45", fontWeight: 600 }}
								>
									Contact Administrator
								</Typography>
								<Box sx={{ flex: 1, borderBottom: "1px solid #b8d4d1" }}></Box>
							</Box>

							{/* Contact Info Box */}
							<Box
								sx={{
									bgcolor: "#f8f9fc",
									borderRadius: 2,
									p: 3,
									mb: 3,
									border: "1px solid #e1e5eb",
								}}
							>
								<Typography
									variant="body1"
									sx={{ color: "#1e4946", mb: adminEmail ? 2 : 0 }}
								>
									Please contact the administrator to unblock your IP address.
								</Typography>

								{/* Email */}
								{adminEmail && (
									<Box
										component="li"
										className="d-flex align-items-center justify-content-center py-2"
										sx={{ color: "#003b38", listStyle: "none" }}
									>
										<MyEmailIcon
											size={22}
											color="#006d66"
										/>
										<Typography variant="h6" sx={{ fontWeight: 600 }}>
											<strong>Email: </strong>
											<Link to={`mailto:${adminEmail}`}
												style={{ color: "#006d66", textDecoration: "none" }}
											>
												{adminEmail}
											</Link>
										</Typography>
									</Box>
								)}
							</Box>

							{/* Warning Box */}
							<Box
								className="d-flex align-items-center justify-content-center"
								sx={{
									bgcolor: "#e9f0ff",
									border: "1px solid #b8d4d1",
									borderRadius: 2,
									p: 2,
								}}
							>
								<MyWarningIcon
									size={22}
									color="#005a56"
									style={{ marginRight: 10, flexShrink: 0 }}
								/>
								<Typography
									variant="body1"
									sx={{ color: "#0d4b45", textAlign: "left" }}
								>
									Do not attempt to bypass this restriction. All attempts are logged.
								</Typography>
							</Box>
						</CardContent>

						{/* Footer - REPLACED WITH YOUR REQUESTED STRUCTURE */}
						<div className="text-center">
							<p className="fs-6 mb-3">
								© {new Date().getFullYear()} Qbot. All Rights Reserved.
							</p>
							<p className="text-muted fs-7 d-flex align-items-center justify-content-center">
								Powered by
								<Link
									to="https://netclues.ky"
									target="_blank"
									rel="noopener noreferrer"
									className="ms-2 d-inline-flex align-items-center"
								>
									<img src={netcluesLogo} alt="Netclues" className="h-20px" />
								</Link>
							</p>
						</div>
					</Card>
				)}
			</Container>

			{/* Animations */}
			<style>{`
				@keyframes blobPulse {
					0%, 100% { opacity: .6; transform: scale(1); }
					50% { opacity: 1; transform: scale(1.15); }
				}

				@keyframes scaleIn {
					0% { transform: scale(.7); opacity: 0; }
					100% { transform: scale(1); opacity: 1; }
				}
			`}</style>
		</Box>
	);
};

export default IpBlockedPage;